/**
 * DetailPanel — Compound Component pra a ficha 360° de produto da página
 * /operacoes.
 *
 * Uso típico (layout default):
 *   <DetailPanel row={selected} onClose={...} {...outrasProps} />
 *
 * Uso avançado (re-ordenar/substituir tabs):
 *   <DetailPanel row={selected} onClose={...}>
 *     <DetailPanel.Header />
 *     <DetailPanel.Tabs>
 *       <DetailPanel.Overview />
 *       <DetailPanel.Sales />
 *       <DetailPanel.Stock />
 *       <DetailPanel.Registry />
 *       <DetailPanel.Outbounds />
 *       <DetailPanel.Inbounds />
 *     </DetailPanel.Tabs>
 *   </DetailPanel>
 *
 * Estado interno (activeTab, paginação outbounds/inbounds, fetchers) vive em
 * DetailPanelContext. Tabs e Header consomem via useDetailPanel().
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft } from 'shared/icons';
import { api } from '../../../../services/api';
import { KbdHint } from '../KbdHint.jsx';
import { TBtn } from '../primitives';
import { useTokens } from '../tokens';
import { Header } from './Header.jsx';
import { DETAIL_TAB_STORAGE_KEY, DETAIL_TABS, LoadLastTab, useLazyFetch, VALID_TAB_IDS } from './internals.jsx';
import { Inbounds } from './tabs/Inbounds.jsx';
import { Outbounds } from './tabs/Outbounds.jsx';
import { Overview } from './tabs/Overview.jsx';
import { Registry } from './tabs/Registry.jsx';
import { Sales } from './tabs/Sales.jsx';
import { Stock } from './tabs/Stock.jsx';

const DetailPanelContext = createContext(null);

export function useDetailPanel() {
    const ctx = useContext(DetailPanelContext);
    if (!ctx) {
        throw new Error('Sub-componente do DetailPanel deve ser usado dentro de <DetailPanel>');
    }
    return ctx;
}

/**
 * Orchestrator principal — gerencia tab ativa, fetchers lazy de
 * outbounds/inbounds, paginação, derivados (forecast/coverage) e expõe
 * tudo via Context para os sub-componentes.
 *
 * Caller continua chamando <DetailPanel row={...} {...props} /> exatamente
 * como antes. Se nenhum children for passado, renderiza layout default
 * (Header + todos os tabs).
 */
export function DetailPanel({
    row,
    onClose,
    onAddCart,
    onPrev,
    onNext,
    hasNav,
    onUpdateRow,
    warehouseNameById,
    allMlbRows = [],
    presetIdentifier = 'sku',
    periodStartIso = null,
    periodEndIso = null,
    children,
}) {
    const T = useTokens();
    // Persiste a aba ATIVA — mesmo ao trocar de produto (via setas ou nova seleção)
    // ou ao reabrir o painel, a aba que estava aberta volta. Source of truth é
    // localStorage pra sobreviver F5; setTab atualiza ambos.
    const [tab, setTabState] = useState(LoadLastTab);
    const setTab = useCallback((next) => {
        if (!VALID_TAB_IDS.has(next)) return;
        setTabState(next);
        try {
            localStorage.setItem(DETAIL_TAB_STORAGE_KEY, next);
        } catch {
            // ignore (private mode, quota)
        }
    }, []);

    // Hooks de fetch precisam vir ANTES do early-return (Rules of Hooks).
    const sku = row?.sku || '';
    const [outPage, setOutPage] = useState(1);
    const [outPageSize, setOutPageSize] = useState(25);
    const [inPage, setInPage] = useState(1);
    const [inPageSize, setInPageSize] = useState(25);

    // Reset page quando o SKU muda (sem useEffect).
    const outResetKey = `out:${sku}`;
    const [outResetState, setOutResetState] = useState({ key: outResetKey, applied: true });
    if (outResetState.key !== outResetKey) {
        setOutResetState({ key: outResetKey, applied: true });
        setOutPage(1);
    }
    const inResetKey = `in:${sku}`;
    const [inResetState, setInResetState] = useState({ key: inResetKey, applied: true });
    if (inResetState.key !== inResetKey) {
        setInResetState({ key: inResetKey, applied: true });
        setInPage(1);
    }

    const outboundsState = useLazyFetch({
        enabled: !!row && tab === 'outbounds',
        key: row && tab === 'outbounds' ? `out:${sku}:${outPage}:${outPageSize}` : null,
        fetcher: () =>
            api.getOutbounds({
                search: sku,
                limit: outPageSize,
                offset: (outPage - 1) * outPageSize,
            }),
    });
    const inboundsState = useLazyFetch({
        enabled: !!row && tab === 'inbounds',
        key: row && tab === 'inbounds' ? `in:${sku}:${inPage}:${inPageSize}` : null,
        fetcher: () =>
            api.getWmsInboundTasks({
                search: sku,
                limit: inPageSize,
                offset: (inPage - 1) * inPageSize,
            }),
    });

    // Derivados — só calcula se row existe (evita NaN no early-return abaixo).
    const forecast = row ? Math.round((row.best7 || 0) * 7 + (row.avg7 || 0) * 15) : 0;
    const coverage = row?.coverage || 0;
    const coverageColor = coverage < 14 ? T.warn : coverage < 30 ? T.ink : T.ok;

    const value = useMemo(
        () => ({
            row,
            T,
            tab,
            setTab,
            tabs: DETAIL_TABS,
            // Callbacks externos
            onClose,
            onAddCart,
            onPrev,
            onNext,
            hasNav,
            onUpdateRow,
            warehouseNameById,
            allMlbRows,
            presetIdentifier,
            // Range UTC ISO do período ativo — tabs que mostram séries diárias
            // (Sales.DailySalesChart) clipam pra esse intervalo em vez de
            // mostrar os 90d completos do snapshot.
            periodStartIso,
            periodEndIso,
            // Derivados pra footer + outras tabs
            forecast,
            coverage,
            coverageColor,
            // Outbounds
            outboundsState,
            outPage,
            outPageSize,
            setOutPage,
            setOutPageSize: (n) => {
                setOutPageSize(n);
                setOutPage(1);
            },
            // Inbounds
            inboundsState,
            inPage,
            inPageSize,
            setInPage,
            setInPageSize: (n) => {
                setInPageSize(n);
                setInPage(1);
            },
        }),
        [
            row,
            T,
            tab,
            setTab,
            onClose,
            onAddCart,
            onPrev,
            onNext,
            hasNav,
            onUpdateRow,
            warehouseNameById,
            allMlbRows,
            presetIdentifier,
            periodStartIso,
            periodEndIso,
            forecast,
            coverage,
            coverageColor,
            outboundsState,
            outPage,
            outPageSize,
            inboundsState,
            inPage,
            inPageSize,
        ],
    );

    if (!row) {
        return (
            <div
                style={{
                    flex: 1,
                    display: 'grid',
                    placeItems: 'center',
                    color: T.muted,
                    fontSize: 13,
                    fontFamily: T.sans,
                    background: T.bgSoft,
                }}
            >
                <div style={{ textAlign: 'center', lineHeight: 2 }}>
                    <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}>
                        <ArrowLeft size={28} color={T.subtle} />
                    </div>
                    Selecione um produto para ver a ficha 360°
                </div>
            </div>
        );
    }

    return (
        <DetailPanelContext.Provider value={value}>
            <DetailPanelShell>{children ?? <DefaultLayout />}</DetailPanelShell>
        </DetailPanelContext.Provider>
    );
}

/**
 * Container scrollável + handler de navegação por teclado:
 *   ↑/↓ : próxima/anterior row (mesmo que botões < >)
 *   ←/→ : próxima/anterior aba
 *   Apenas com foco DENTRO do painel — outros teclados (scroll, etc) seguem
 *   comportamento default do browser. Container tabIndex=-1 pra permitir focus
 *   programático (selecionar row foca o painel).
 */
function DetailPanelShell({ children }) {
    const { T, tab, setTab, tabs, onPrev, onNext, hasNav } = useDetailPanel();
    const ref = useRef(null);

    const handleKeyDown = useCallback(
        (e) => {
            // Só intercepta quando o evento vem de DENTRO do painel — o
            // próprio container tem onKeyDown, então React fluxo de eventos
            // garante isso (não precisa checar focus globalmente).
            // Ignora se o user está digitando em input/textarea (deixa o
            // browser tratar setas dentro do campo).
            const tag = String(e.target?.tagName || '').toUpperCase();
            if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
            if (e.key === 'ArrowDown') {
                if (hasNav && onNext) {
                    e.preventDefault();
                    onNext();
                }
            } else if (e.key === 'ArrowUp') {
                if (hasNav && onPrev) {
                    e.preventDefault();
                    onPrev();
                }
            } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                const list = Array.isArray(tabs) ? tabs : [];
                const idx = list.findIndex((t) => t.id === tab);
                if (idx === -1) return;
                const nextIdx =
                    e.key === 'ArrowRight' ? (idx + 1) % list.length : (idx - 1 + list.length) % list.length;
                e.preventDefault();
                setTab(list[nextIdx].id);
            }
        },
        [tab, setTab, tabs, onPrev, onNext, hasNav],
    );

    // Quando o painel recebe um row novo (seleção mudou), foca o container
    // pra setas serem capturadas sem o user precisar clicar primeiro. Não
    // mexe se já há outro elemento focado dentro (ex: input do TagsEditor).
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (!el.contains(document.activeElement)) {
            el.focus({ preventScroll: true });
        }
    }, []);

    return (
        <div
            ref={ref}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            data-faid="Detail_Panel_Container_Operacoes"
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                background: T.bg,
                overflow: 'hidden',
                outline: 'none',
            }}
        >
            {children}
        </div>
    );
}

/**
 * Layout default: Header (uuid+nome+tags+nav+tabs) + body com a tab ativa +
 * Footer (cobertura/forecast/+carrinho). Renderizado quando o caller não
 * passa children explícitos.
 */
function DefaultLayout() {
    return (
        <>
            <Header />
            <Body />
            <Footer />
        </>
    );
}

/**
 * Body: renderiza a tab ativa. Usado pelo layout default; quem quiser
 * controle total pode usar <DetailPanel.Overview />, etc, diretamente.
 */
function Body() {
    const { tab } = useDetailPanel();
    return (
        <div style={{ flex: 1, overflow: 'auto', padding: 18 }}>
            {tab === 'overview' && <Overview />}
            {tab === 'vendas' && <Sales />}
            {tab === 'estoque' && <Stock />}
            {tab === 'cadastro' && <Registry />}
            {tab === 'outbounds' && <Outbounds />}
            {tab === 'inbounds' && <Inbounds />}
        </div>
    );
}

/**
 * Footer: cobertura + forecast + botão "+ carrinho". Padronizado com a
 * altura T.footerH (50px) pra alinhar com DataTable footer e Toolbar header
 * em Split vertical / Drawer.
 */
function Footer() {
    const { T, onAddCart, row } = useDetailPanel();
    return (
        <div
            style={{
                padding: '0 18px',
                // T.footerH (50px) padronizado com DataTable footer e Toolbar
                // headerH — alinha visualmente em Split vertical / Drawer.
                height: T.footerH,
                borderTop: `1px solid ${T.line}`,
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                background: T.bgSoft,
                flexShrink: 0,
            }}
        >
            {/* Footer agora mostra atalhos de navegação por teclado em vez de
                cobertura/forecast (esses já aparecem na aba Visão Geral). User
                explicitamente pediu: kbd hints como referência rápida do que
                dá pra fazer com o painel focado. */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                <KbdHint keys={['↑', '↓']} compact label="trocar SKU" title="Setas ↑/↓ mudam de produto" />
                <KbdHint keys={['←', '→']} compact label="trocar aba" title="Setas ←/→ cicla as abas" />
            </span>
            <div style={{ flex: 1 }} />
            {onAddCart && (
                <TBtn variant="primary" size="sm" onClick={() => onAddCart?.(row)}>
                    + carrinho
                </TBtn>
            )}
        </div>
    );
}

/**
 * Container <DetailPanel.Tabs>...</DetailPanel.Tabs> — só renderiza a tab
 * ativa entre seus filhos. Cada filho declara qual `id` representa via prop
 * `data-tab-id` (ou usamos um marker via static prop).
 *
 * Como cada Tab.<X> componente já é só um wrapper sobre o conteúdo, optamos
 * por uma abordagem mais simples: o container <Tabs> chama um render dispatch
 * conforme `tab` ativo. Caller pode trocar a ordem mas, na prática, o que
 * decide qual tab aparece é o `tab` no Context.
 */
function Tabs({ children }) {
    return <div style={{ flex: 1, overflow: 'auto', padding: 18 }}>{children}</div>;
}

// Cada tab decide internamente se deve renderizar a si mesma com base no
// `tab` ativo do Context. Isso permite que caller declare ordem custom dentro
// de <DetailPanel.Tabs> sem nós precisarmos parsear filhos.

// Anexa sub-componentes como propriedades (Compound Component API).
DetailPanel.Header = Header;
DetailPanel.Tabs = Tabs;
DetailPanel.Overview = Overview;
DetailPanel.Sales = Sales;
DetailPanel.Stock = Stock;
DetailPanel.Registry = Registry;
DetailPanel.Outbounds = Outbounds;
DetailPanel.Inbounds = Inbounds;
DetailPanel.Footer = Footer;
